import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { API_BASE } from "./api";
import DEFAULTS from "./data/site";

const ContentContext = createContext(null);

const CATEGORIES = [
  { id: "basmati", name: "Premium Basmati Rice", icon: "fa-bowl-rice" },
  { id: "biryani", name: "Shahi Biryani Rice", icon: "fa-utensils" },
  { id: "rozana", name: "Rozana Rice", icon: "fa-wheat-awn" },
];

// Icons the live site uses on the Recipes dropdown, keyed by group id.
const RECIPE_ICONS = {
  biryani: "fa-bowl-food",
  indian: "fa-utensils",
  global: "fa-globe",
  healthy: "fa-heart-pulse",
};

export function ContentProvider({ children }) {
  // Render immediately with the bundled content, then swap in whatever the
  // admin has published. Keeps the site up even if the API is unreachable.
  const [content, setContent] = useState(DEFAULTS);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/site-content`)
      .then((r) => r.json())
      .then((d) => {
        if (alive && d && d.success && d.content) {
          setContent({ ...DEFAULTS, ...d.content });
        }
      })
      .catch(() => {
        /* keep the bundled content */
      });
    return () => {
      alive = false;
    };
  }, []);

  const value = useMemo(() => {
    const brands = content.brands || [];
    const base = (content.productImageBase || "").replace(/\/$/, "");

    const productImg = (brand, category, code) =>
      `${base}/${brand.dir}/${category}/${code}.jpg`;

    const findProduct = (code) => {
      for (const brand of brands) {
        for (const cat of CATEGORIES) {
          if ((brand.products?.[cat.id] || []).includes(code)) {
            return {
              code,
              brand,
              category: cat,
              image: productImg(brand, cat.id, code),
            };
          }
        }
      }
      return null;
    };

    const nav = [
      { label: "Home", to: "/", icon: "fa-house" },
      {
        label: "Products Range",
        icon: "fa-seedling",
        mega: brands.map((b) => ({
          title: b.name,
          icon: "fa-crown",
          links: CATEGORIES.map((c) => ({
            label: c.name,
            to: `/${b.slug}#${c.id}`,
            icon: c.icon,
          })),
        })),
      },
      // The shop: everything the admin publishes, with live prices.
      {
        label: "Recipes",
        icon: "fa-utensils",
        submenu: (content.recipeGroups || []).map((g) => ({
          label: g.title,
          to: `/recipes#${g.id}`,
          icon: RECIPE_ICONS[g.id] || "fa-utensils",
        })),
      },
      { label: "Occasions", to: "/occasions", icon: "fa-house" },
      {
        label: "About Us",
        icon: "fa-building",
        submenu: [
          {
            label: "Company Profile",
            to: "/company-profile",
            icon: "fa-file-lines",
          },
          {
            label: "Vision & Mission",
            to: "/vision-mission",
            icon: "fa-bullseye",
          },
          {
            label: "Infrastructure",
            to: "/infrastructure",
            icon: "fa-industry",
          },
          {
            label: "Quality Assurance",
            to: "/quality-assurance",
            icon: "fa-award",
          },
        ],
      },
      { label: "Contact Us", to: "/contact-us", icon: "fa-phone" },
      { label: "Buy Online", to: "/products", icon: "fa-cart-shopping" },
    ];

    return {
      ...content,
      categories: CATEGORIES,
      brandBySlug: (slug) => brands.find((b) => b.slug === slug),
      productImg,
      findProduct,
      nav,
      quickLinks: [
        { label: "Home", to: "/" },
        { label: "Company Profile", to: "/company-profile" },
        { label: "Vision & Mission", to: "/vision-mission" },
        { label: "Infrastructure", to: "/infrastructure" },
        { label: "Quality Assurance", to: "/quality-assurance" },
        { label: "Contact Us", to: "/contact-us" },
      ],
    };
  }, [content]);

  return (
    <ContentContext.Provider value={value}>{children}</ContentContext.Provider>
  );
}

export const useContent = () => useContext(ContentContext);
